import { IMarkdownSettings } from "./defaultMarkdownSettings";
import {
  EditorV3TextBlock,
  IEditorV3TextBlock,
  IEditorV3TextBlockOptionalParams,
} from "./EditorV3TextBlock";
import {
  EditorV3DropListItem,
  EditorV3RenderProps,
  EditorV3Style,
  EditorV3WordPosition,
} from "./interface";
import { renderDropdown } from "./toHtml/renderDropdown";

export interface IEditorV3AtBlockOptionalParams extends IEditorV3TextBlockOptionalParams {
  atListFunction?: (typedString: string) => Promise<EditorV3DropListItem<Record<string, string>>[]>;
  maxAtListLength?: number;
  atData?: Record<string, string>;
}

export interface IEditorV3AtBlock extends IEditorV3TextBlock, IEditorV3AtBlockOptionalParams {}

/**
 * Represents an @-mention block in the EditorV3 rich text editor.
 */
export class EditorV3AtBlock extends EditorV3TextBlock implements IEditorV3AtBlock {
  /**
   * Promise function that will return list of string values to display.
   *
   * @param typedString - Text entered into the block so far
   * @returns Promise of array of dropdown list items
   */
  public atListFunction: (
    typedString: string,
  ) => Promise<EditorV3DropListItem<Record<string, string>>[]> = () =>
    new Promise((resolve) => resolve([]));
  /**
   * Maximum number of items to display in the returned list.
   */
  public maxAtListLength = 10;

  public atData: Record<string, string> = {};

  /**
   * Returns the data representation of the at block.
   *
   * @returns The at block data interface
   */
  get data(): IEditorV3AtBlock {
    const ret: IEditorV3AtBlock = super.data;
    if (Object.keys(this.atData).length > 0) {
      ret.atData = this.atData;
    }
    return ret;
  }

  /**
   * Returns the word positions within the at block.
   *
   * @returns Array of word positions
   */
  get wordPositions(): EditorV3WordPosition[] {
    return [
      {
        line: -1,
        startChar: this.lineStartPosition,
        endChar: this.lineStartPosition + this.text.length,
        isLocked: this.isLocked === true,
      },
    ];
  }

  /**
   * Creates a new EditorV3AtBlock instance.
   *
   * @param arg - The source data (interface, HTML element, fragment, or string)
   * @param forcedParams - Optional parameters to force on the block
   */
  constructor(
    arg: IEditorV3AtBlock | HTMLSpanElement | DocumentFragment | string,
    forcedParams?: IEditorV3AtBlockOptionalParams,
  ) {
    // Usual text block constructor
    super(arg, forcedParams);
    // Force type
    this.type = "at";
    // Check for at specific information
    if (typeof arg === "string") {
      this.furtherMarkdown(forcedParams?.markdownSettings);
    } else if (arg instanceof HTMLSpanElement) {
      this.furtherHtml(arg);
    } else if (arg instanceof DocumentFragment) {
      if (arg.childNodes.length !== 1)
        throw new Error("EditorV3AtBlock:Constructor: DocumentFragment must have 1 child node");
      else if (!(arg.childNodes[0] instanceof HTMLSpanElement))
        throw new Error("EditorV3AtBlock:Constructor: Child node must be HTMLSpanElement");
      // Read element
      this.furtherHtml(arg.childNodes[0] as HTMLSpanElement);
    } else {
      if (arg.atListFunction) this.atListFunction = arg.atListFunction;
      if (arg.maxAtListLength) this.maxAtListLength = arg.maxAtListLength;
      if (arg.atData) {
        this.atData = arg.atData;
      }
    }
    // Force any parameters
    if (forcedParams) {
      if (forcedParams.atListFunction) this.atListFunction = forcedParams.atListFunction;
      if (forcedParams.maxAtListLength) this.maxAtListLength = forcedParams.maxAtListLength;
      if (forcedParams.atData) this.atData = forcedParams.atData;
    }
  }

  /**
   * Read extra data from HTML element.
   *
   * @param arg - The HTML span element to extract data from
   */
  private furtherHtml(arg: HTMLSpanElement) {
    // Copy dataset to atFactory
    Object.keys(arg.dataset).forEach((key) => {
      if (!["styleName", "isLocked", "type", "text", "lineStartPosition"].includes(key)) {
        this.atData[key] = arg.dataset[key] as string;
      }
    });
  }

  /**
   * Extract markdown information from block text.
   *
   * @param arg - Optional markdown settings to use
   */
  private furtherMarkdown(arg?: IMarkdownSettings) {
    const markdownSettings = arg ?? this._defaultContentProps.markdownSettings;
    this.atData = {};
    /* istanbul ignore else */
    if (
      this.text.startsWith(markdownSettings.atStartTag) &&
      this.text.endsWith(markdownSettings.atEndTag) &&
      this.text.includes(markdownSettings.dropDownSelectedValueTag)
    ) {
      // Remove start and end tags
      this.text = this.text.slice(
        markdownSettings.atStartTag.length,
        -markdownSettings.atEndTag.length,
      );
      // Find the dropdown selected value tag
      const dd = this.text.indexOf(markdownSettings.dropDownSelectedValueTag);
      // Get the data text
      const atDataText = this.text.slice(dd + markdownSettings.dropDownSelectedValueTag.length);
      try {
        this.atData = JSON.parse(atDataText);
      } catch {
        this.atData = {};
      }
      // Get the text/style/label
      const current = this.text.slice(0, dd);
      this.markdownStyleLabel(current, markdownSettings);
    }
  }

  /**
   * Converts the at block to an HTML document fragment.
   *
   * @param renderProps - The rendering properties
   * @param style - Optional style to apply
   * @returns Document fragment containing the rendered HTML
   */
  public toHtml(renderProps: EditorV3RenderProps, style?: EditorV3Style): DocumentFragment {
    // Create holder
    const ret = new DocumentFragment();
    if (renderProps.currentEl) renderProps.currentEl.append(ret);
    const span = document.createElement("span");
    ret.appendChild(span);
    span.classList.add("aiev3-tb", "at-block");
    span.dataset.lineStartPosition = this.lineStartPosition.toString();
    span.dataset.type = "at";

    // Add Text node
    const textNode = document.createTextNode(
      this.text
        .replaceAll(/[\u2009-\u200f]/g, "")
        .replace(/^ /, "\u00a0")
        .replace(/ $/, "\u00a0"),
    );

    span.appendChild(textNode);
    // Add style
    this.applyStyle(span, style);
    // Add label
    if (this.label) span.title = this.label;

    // Add locked status
    /* istanbul ignore else */
    if (this.isLocked || !this.isActive) {
      span.classList.add("is-locked");
      span.dataset.isLocked = "true";
      span.contentEditable = "false";
      // Add any other data items from the at item
      /* istanbul ignore else */
      if (this.atData)
        Object.keys(this.atData).forEach((key) => {
          span.dataset[key] = this.atData[key] as string;
        });
    } else if (this.isActive) {
      span.classList.add("is-active");

      // Throttle render
      let renderTimeout: number | null = null;
      /* istanbul ignore else */
      if (!renderTimeout) {
        span.classList.add("show-dropdown");
        renderTimeout = window.setTimeout(
          () =>
            renderDropdown(span, this.text, {
              ...renderProps,
              atListFunction: renderProps.atListFunction ?? this.atListFunction,
              maxAtListLength: renderProps.maxAtListLength ?? this.maxAtListLength,
            }),
          100,
        );
      }
    }

    // Add to current element if specified
    if (renderProps.currentEl) renderProps.currentEl.append(ret);
    // Always return
    return ret;
  }

  /**
   * Converts the at block to markdown string representation.
   *
   * @param markdownSettings - The markdown settings to use
   * @returns Markdown string representation
   */
  public toMarkdown(markdownSettings = this._defaultContentProps.markdownSettings): string {
    // Set base text
    let text = markdownSettings.atStartTag;
    // Update text with style and label
    text += this.toMarkdownStyleLabel(markdownSettings);
    // Add text
    text += this.text;
    // Add atData
    text += markdownSettings.dropDownSelectedValueTag + JSON.stringify(this.atData);
    // Complete tag
    text += markdownSettings.atEndTag;
    return text;
  }
}
